import React, { useEffect, useState } from "react";
import { IconButton, Tooltip } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBellSlash,
  faFileCheck,
  faXmark,
} from "@fortawesome/pro-regular-svg-icons";
import { faBellSlash as faBellSlashSolid } from "@fortawesome/pro-solid-svg-icons";
import { RequisitionRow } from "../../recoil/types";
import { NewRequisitionType } from "../NewRequisition/types";
import { RedrawAlertTabContent } from "../Redraw/RedrawAlertTabContent";
import { ActionsNeededAlertTabContent } from "../Grid/ActionsNeededAlertTabContent";
import {
  CfcEligibilityDialog,
  CfcEligibilityHeader,
  CfcEligibilityHeaderTitle,
  CfcEligibilityBody,
  CfcEligibilityHeading,
  CfcEligibilityDescription,
  CfcEligibilityDescriptionBold,
  CfcEligibilityDescriptionAlert,
  CfcEligibilityTable,
  CfcEligibilityTableHeader,
  CfcEligibilityTableHeaderCell,
  CfcEligibilityTableBody,
  CfcEligibilityRow,
  CfcEligibilityPatientName,
  CfcEligibilityDate,
  CfcEligibilityActions,
  CfcEligibilityWarning,
  CfcEligibilityWarningRow,
  CfcEligibilityFooter,
  CfcEligibilityActionButton,
  CfcEligibilityTabsContainer,
  CfcEligibilityTabs,
  CfcEligibilityTab,
  CfcEligibilityTabAlert,
  CfcEligibilitySupportLink,
  CfcEligibilityTabLabel,
  CfcEligibilityTabEyebrow,
} from "./CfcAlert.styles";
import { formatCfcCollectByDateLong } from "./cfcGridUtils";

const GENETIC_COUNSELING_EMAIL = "gc@billiontoone.com";
const GENETIC_COUNSELING_PHONE = "+1 (650) 460-2551";

export interface CfcEligiblePatient {
  reqIdentifier: string;
  patientName: string;
  collectByDate: string;
  rowData: RequisitionRow;
}

export enum AlertTab {
  ActionsNeeded = 0,
  RedrawsRequests = 1,
  UnityConfirmEligibility = 2,
}

const CFC_ELIGIBILITY_DESCRIPTION = (
  <>
    The patient(s) below have met eligibility criteria for{" "}
    <CfcEligibilityDescriptionBold>
      Unity Confirm™, circulating fetal cell confirmatory testing
    </CfcEligibilityDescriptionBold>
    . It is critical that the sample{" "}
    <CfcEligibilityDescriptionAlert>
      is drawn by the indicated date
    </CfcEligibilityDescriptionAlert>{" "}
    in order to remain eligible for Unity Confirm.
  </>
);

const CFC_ELIGIBILITY_WARNING =
  "Samples collected after this date will be rejected, due to the low likelihood of capturing intact fetal cells past 15w6d gestation.";

const SUPPORT_CONTACT_COPY = (
  <>
    For questions about Unity Confirm please contact{" "}
    <CfcEligibilitySupportLink href={`mailto:${GENETIC_COUNSELING_EMAIL}`}>
      {GENETIC_COUNSELING_EMAIL}
    </CfcEligibilitySupportLink>
    <br />
    or reach out to us at {GENETIC_COUNSELING_PHONE}
  </>
);

interface CfcEligibilityPatientRowProps {
  patient: CfcEligiblePatient;
  onOrderCfc: (row: RequisitionRow) => void;
  onMute: (row: RequisitionRow) => void;
}

const CfcEligibilityPatientRow = ({
  patient,
  onOrderCfc,
  onMute,
}: CfcEligibilityPatientRowProps): React.ReactElement => {
  const isMuted = patient.rowData.isCfcAlertDismissed;
  return (
    <CfcEligibilityRow key={patient.reqIdentifier} role="row">
      <CfcEligibilityPatientName role="cell">
        {patient.patientName}
      </CfcEligibilityPatientName>
      <CfcEligibilityDate role="cell">
        {formatCfcCollectByDateLong(patient.collectByDate)}
      </CfcEligibilityDate>
      <CfcEligibilityActions role="cell">
        <Tooltip
          arrow
          placement="top"
          title={
            isMuted
              ? "Alerts stopped for this patient"
              : "Stop alerts about this patient"
          }
        >
          <span>
            <CfcEligibilityActionButton
              type="button"
              disabled={isMuted}
              aria-label={
                isMuted
                  ? `Alerts stopped for ${patient.patientName}`
                  : `Mute Unity Confirm alert for ${patient.patientName}`
              }
              onClick={() => {
                if (!isMuted) onMute(patient.rowData);
              }}
            >
              <FontAwesomeIcon
                aria-hidden
                icon={isMuted ? faBellSlashSolid : faBellSlash}
              />
            </CfcEligibilityActionButton>
          </span>
        </Tooltip>
        <Tooltip arrow placement="top" title="Order Unity Confirm™">
          <CfcEligibilityActionButton
            type="button"
            aria-label={`Order Unity Confirm for ${patient.patientName}`}
            onClick={() => onOrderCfc(patient.rowData)}
          >
            <FontAwesomeIcon aria-hidden icon={faFileCheck} />
          </CfcEligibilityActionButton>
        </Tooltip>
      </CfcEligibilityActions>
    </CfcEligibilityRow>
  );
};

interface CfcEligibilityModalProps {
  open: boolean;
  onClose: () => void;
  eligiblePatients: CfcEligiblePatient[];
  onOrderCfc: (row: RequisitionRow) => void;
  onMuteCfc: (row: RequisitionRow) => void;
  initialTab?: AlertTab;
  redrawDrafts: NewRequisitionType[];
  onRedrawViewOrders: () => void;
  onRedrawRowClick: (patientName: string) => void;
  onRedrawEditOrder?: (order: NewRequisitionType) => void;
  onRedrawDismiss?: (order: NewRequisitionType) => void;
}

export const CfcEligibilityModal = ({
  open,
  onClose,
  eligiblePatients,
  onOrderCfc,
  onMuteCfc,
  initialTab = AlertTab.UnityConfirmEligibility,
  redrawDrafts,
  onRedrawViewOrders,
  onRedrawRowClick,
  onRedrawEditOrder,
  onRedrawDismiss,
}: CfcEligibilityModalProps): React.ReactElement => {
  const [activeTab, setActiveTab] = useState<AlertTab>(initialTab);
  const [redrawBusy, setRedrawBusy] = useState(false);

  useEffect(() => {
    if (open) setActiveTab(initialTab);
  }, [initialTab, open]);

  const handleClose = () => {
    if (redrawBusy) return;
    onClose();
  };

  const handleTabChange = (_: unknown, newValue: AlertTab) => {
    if (redrawBusy) return;
    setActiveTab(newValue);
  };

  return (
    <CfcEligibilityDialog
      open={open}
      onClose={handleClose}
      fullWidth
      aria-labelledby="cfc-eligibility-alerts-title"
    >
      <CfcEligibilityHeader>
        <CfcEligibilityHeaderTitle id="cfc-eligibility-alerts-title">
          ALERTS
        </CfcEligibilityHeaderTitle>
        <IconButton
          aria-label="Close alerts modal"
          disabled={redrawBusy}
          onClick={handleClose}
          size="small"
        >
          <FontAwesomeIcon aria-hidden icon={faXmark} />
        </IconButton>
      </CfcEligibilityHeader>
      <CfcEligibilityTabsContainer>
        <CfcEligibilityTabs
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons={false}
          $alertSelected={activeTab === AlertTab.UnityConfirmEligibility}
        >
          <CfcEligibilityTabAlert
            disabled={redrawBusy}
            value={AlertTab.UnityConfirmEligibility}
            label={
              <CfcEligibilityTabLabel>
                {activeTab !== AlertTab.UnityConfirmEligibility && (
                  <CfcEligibilityTabEyebrow>
                    Time-sensitive
                  </CfcEligibilityTabEyebrow>
                )}
                Eligible Unity Confirm Patient(s)
              </CfcEligibilityTabLabel>
            }
          />
          <CfcEligibilityTab
            disabled={redrawBusy}
            value={AlertTab.RedrawsRequests}
            label="Redraws Requested"
          />
          <CfcEligibilityTab
            disabled={redrawBusy}
            value={AlertTab.ActionsNeeded}
            label="Actions Needed"
          />
        </CfcEligibilityTabs>
      </CfcEligibilityTabsContainer>
      {activeTab === AlertTab.ActionsNeeded && (
        <CfcEligibilityBody>
          <ActionsNeededAlertTabContent onRowClick={onClose} />
        </CfcEligibilityBody>
      )}
      {activeTab === AlertTab.RedrawsRequests && (
        <CfcEligibilityBody>
          <RedrawAlertTabContent
            redrawDrafts={redrawDrafts}
            onViewOrders={onRedrawViewOrders}
            onRowClick={onRedrawRowClick}
            onEditOrder={onRedrawEditOrder}
            onDismiss={onRedrawDismiss}
            resetSignal={open}
            onBusyChange={setRedrawBusy}
          />
        </CfcEligibilityBody>
      )}
      {activeTab === AlertTab.UnityConfirmEligibility && (
        <CfcEligibilityBody>
          <CfcEligibilityHeading>TIME SENSITIVE ACTION:</CfcEligibilityHeading>
          <CfcEligibilityDescription>
            {CFC_ELIGIBILITY_DESCRIPTION}
          </CfcEligibilityDescription>
          <CfcEligibilityTable
            role="table"
            aria-label="Eligible Unity Confirm patients"
          >
            <CfcEligibilityTableHeader role="row">
              <CfcEligibilityTableHeaderCell role="columnheader">
                PATIENT
              </CfcEligibilityTableHeaderCell>
              <CfcEligibilityTableHeaderCell role="columnheader">
                DRAW SAMPLE BY
              </CfcEligibilityTableHeaderCell>
              <CfcEligibilityTableHeaderCell role="columnheader">
                ACTIONS
              </CfcEligibilityTableHeaderCell>
            </CfcEligibilityTableHeader>
            <CfcEligibilityTableBody role="rowgroup">
              {eligiblePatients.map((patient) => (
                <CfcEligibilityPatientRow
                  key={patient.reqIdentifier}
                  patient={patient}
                  onOrderCfc={onOrderCfc}
                  onMute={onMuteCfc}
                />
              ))}
            </CfcEligibilityTableBody>
          </CfcEligibilityTable>
          <CfcEligibilityWarningRow>
            <CfcEligibilityWarning>
              {CFC_ELIGIBILITY_WARNING}
            </CfcEligibilityWarning>
          </CfcEligibilityWarningRow>
          <CfcEligibilityFooter>{SUPPORT_CONTACT_COPY}</CfcEligibilityFooter>
        </CfcEligibilityBody>
      )}
    </CfcEligibilityDialog>
  );
};
